import { useEffect, useState } from "react";
import { authAxios } from "../../utils/session_utils";
import { SourceDeal } from "../data/sourceDeals";

interface UseSourceDealsResult {
  data: SourceDeal[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetches Media Plan Builder source deals from Snowflake via the
 * /snowflake/media-plan/source-deals endpoint.
 *
 * No params → returns the rolling 4-quarter window (Q4 prior year through
 * Q3 current sell year, configured server-side). Each row carries its own
 * `quarter` and `year` so the UI can group/filter by tab.
 */
export function useSourceDeals(): UseSourceDealsResult {
  const [data, setData] = useState<SourceDeal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    authAxios({
      method: "GET",
      url: `${process.env.REACT_APP_BACK_END_API}/snowflake/media-plan/source-deals`,
    })
      .then(response => {
        if (cancelled) return;
        const rows = (response.data?.rows as SourceDeal[]) || [];
        // API doesn't return dealKey — assign sequential keys so React can
        // properly reconcile rows. Without unique keys, <input> elements with
        // `defaultValue` get reused across rows and show stale values.
        const withKeys = rows.map((d, i) => ({
          ...d,
          dealKey: d.dealKey || `D-NEW-${String(i + 1).padStart(4, "0")}`,
        }));
        setData(withKeys);
      })
      .catch(err => {
        if (cancelled) return;
        console.error("Error fetching source deals", err);
        setError(err?.message || "Failed to load source deals");
        setData([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
}
